import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { MdChevronRight } from "react-icons/md";

import { foldersAbove } from "./folders";
import { ACCENT } from "./accent";
import { Part, PartHead, PartTitle, PartTotal, List, Head, Item, Name, Count, Track, Fill, Figures, DIMMED, shareOf, barWidthOf, amountOf, lossesOf } from "./BreakdownPart";

const INDENT = 16;
const LINES = 8;

const flatten = (nodes) => nodes.filter((node) => !node.ownPages).flatMap((node) => [node, ...flatten(node.children)]);

/**
 * The URL folders of the listed rows, as a tree opened one level at a time: each folder's rows, their loss
 * a month on each of `measures` and their share of the total impact, on a bar. A folder with no impact shows its rows and no bar. Selecting a folder
 * filters the list, highlights its line and dims the others; selecting it again clears the filter.
 * The selected folder is always in view: the folders above it open whenever it changes.
 * Every folder is in one list, eight lines tall whatever the filter leaves, that scrolls inside the part with the selected
 * folder scrolled into view; typing in the filter lists every folder, at any depth, whose path holds the text.
 * An opened folder's own pages are a line that cannot be selected or opened.
 */
export default function PathBreakdown({ folders, total, measures, selectedPath, onSelect }) {
    const lossShown = measures.length > 0;
    const [open, setOpen] = useState(new Set());
    const scroll = useRef(null);
    const [height, setHeight] = useState(null);
    const [query, setQuery] = useState("");
    useEffect(() => {
        if (selectedPath) setOpen((current) => new Set([...current, ...foldersAbove(selectedPath).slice(0, -1)]));
    }, [selectedPath]);
    const toggle = (path) => setOpen((current) => {
        const next = new Set(current);
        if (next.has(path)) next.delete(path); else next.add(path);
        return next;
    });
    const maxImpact = Math.max(...folders.map(({ impact }) => impact), 1);
    const lines = (nodes, depth) => nodes.flatMap((node) => [
        { node, depth },
        ...(open.has(node.path) ? lines(node.children, depth + 1) : []),
    ]);
    const matching = query.trim()
        ? flatten(folders).filter((node) => node.path.toLowerCase().includes(query.trim().toLowerCase()))
            .sort((a, b) => b.impact - a.impact || b.rows - a.rows)
            .map((node) => ({ node: { ...node, name: node.path, children: [] }, depth: 0 }))
        : null;
    const shown = matching ?? lines(folders, 0);
    // A line is taller on a phone, where its bar wraps under it, so eight lines are measured from the first one.
    useLayoutEffect(() => {
        const [head, first] = scroll.current.children;
        if (first) setHeight(head.offsetHeight + LINES * first.offsetHeight);
    }, [shown.length > 0]);
    // Scrolls the list, never the page, so the selected folder is in view.
    useLayoutEffect(() => {
        const list = scroll.current;
        const selected = list.querySelector("[aria-pressed=true]");
        if (!selected) return;
        const line = selected.parentElement;
        if (line.offsetTop < list.scrollTop || line.offsetTop + line.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTop = line.offsetTop - (list.clientHeight - line.offsetHeight) / 2;
        }
    }, [selectedPath, height, open]);

    return (
        <Part>
            <PartHead>
                <PartTitle>{lossShown ? "Est. loss by path" : "Issues by path"}</PartTitle>
                {lossShown && <PartTotal>{lossesOf(total.lostPerMonth, measures)} /month</PartTotal>}
                <Search type="search" placeholder={`Filter ${flatten(folders).length} folders`} value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Filter folders" />
            </PartHead>
            <Scroll ref={scroll} style={{ height: height ?? undefined }}>
                <StickyHead aria-hidden>
                    <span />
                    <Count>Issues</Count>
                    {lossShown && <span />}
                    {lossShown && (
                        <HeadFigures $measures={measures.length}>
                            {measures.map(({ key, label }) => <span key={key}>{label}/<wbr />month</span>)}
                            <span />
                        </HeadFigures>
                    )}
                </StickyHead>
                {shown.map(({ node, depth }) => (node.ownPages ? (
                    <Line key={`${node.path} own pages`}>
                        <OwnPages as="div" $dimmed={selectedPath !== null}>
                            <OwnPagesName $depth={depth}>{node.name}</OwnPagesName>
                            <Count>{node.rows.toLocaleString()}</Count>
                            {lossShown && (node.impact > 0 ? <Track><Fill style={{ width: barWidthOf(node.impact, maxImpact) }} /></Track> : <span />)}
                            {lossShown && <FolderFigures node={node} total={total} measures={measures} />}
                        </OwnPages>
                    </Line>
                ) : (
                    <Line key={node.path}>
                        {node.children.length > 0 && (
                            <Toggle type="button" $depth={depth} $open={open.has(node.path)} $dimmed={selectedPath !== null && node.path !== selectedPath} aria-expanded={open.has(node.path)}
                                aria-label={`${open.has(node.path) ? "Close" : "Open"} ${node.path}`} onClick={() => toggle(node.path)}>
                                <MdChevronRight />
                            </Toggle>
                        )}
                        <Item type="button" $selected={node.path === selectedPath} $dimmed={selectedPath !== null && node.path !== selectedPath} aria-pressed={node.path === selectedPath} onClick={() => onSelect(node.path)}>
                            <Folder $depth={depth} title={node.path}>{node.name}</Folder>
                            <Count>{node.rows.toLocaleString()}</Count>
                            {lossShown && (node.impact > 0 ? <Track><Fill style={{ width: barWidthOf(node.impact, maxImpact) }} /></Track> : <span />)}
                            {lossShown && <FolderFigures node={node} total={total} measures={measures} />}
                        </Item>
                    </Line>
                )))}
            </Scroll>
            {matching && matching.length === 0 && <Empty>No folder holds "{query.trim()}"</Empty>}
        </Part>
    );
}

function FolderFigures({ node, total, measures }) {
    return (
        <Figures $measures={measures.length}>
            {measures.map(({ key }) => <strong key={key}>{amountOf(node.lostPerMonth[key])}</strong>)}
            <span>{node.impact > 0 ? shareOf(node.impact, total.impact) : ""}</span>
        </Figures>
    );
}

const Line = styled.li`
    position: relative;
`;

const Toggle = styled.button`
    position: absolute;
    z-index: 1;
    top: 50%;
    left: ${({ $depth }) => $depth * INDENT - 2}px;
    display: flex;
    width: 18px;
    height: 18px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: none;
    color: #5a6473;
    opacity: ${({ $dimmed }) => ($dimmed ? DIMMED : 1)};
    cursor: pointer;
    transform: translateY(-50%);
    svg { width: 18px; height: 18px; transition: transform 0.15s; transform: rotate(${({ $open }) => ($open ? 90 : 0)}deg); }
    &:hover { background: #e4e7ec; }
    @media (max-width: 600px) { top: 13px; }
`;

const Folder = styled(Name)`
    display: block;
    padding-left: ${({ $depth }) => $depth * INDENT + 20}px;
    font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, monospace;
    font-size: 0.75rem;
`;

const OwnPages = styled(Item)`
    cursor: default;
    &:hover { background: none; opacity: ${({ $dimmed }) => ($dimmed ? DIMMED : 1)}; }
`;

const OwnPagesName = styled(Folder)`
    font-family: inherit;
    font-size: 0.8rem;
    font-style: italic;
    color: #5a6473;
`;

const HeadFigures = styled(Figures)`
    font-size: inherit;
`;

const StickyHead = styled(Head)`
    position: sticky;
    z-index: 2;
    top: 0;
    background: #fff;
`;

/** At the right of the title line; on a phone, where it does not fit beside the title, under it at full width. */
const Search = styled.input`
    box-sizing: border-box;
    flex: 0 1 200px;
    min-width: 140px;
    margin: -4px 0 -4px auto;
    padding: 3px 8px;
    border: 1px solid #e4e7ec;
    border-radius: 6px;
    font: inherit;
    font-size: 0.8rem;
    color: #0f1419;
    &:focus { outline: none; border-color: ${ACCENT}; }
    @media (max-width: 600px) { flex-basis: 100%; margin: 0; }
`;

const Scroll = styled(List)`
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 8px;
    margin-left: -8px;
    margin-right: -8px;
`;

const Empty = styled.p`
    margin: 8px 0 0;
    font-size: 0.8rem;
    color: #5a6473;
`;
